import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError, getMatchAnalysis, postAdminAnalyze } from '../../api/client';
import type { MatchAnalysis } from '../../api/types';
import MarkdownView from './MarkdownView';

export default function AnalysisPanel({ matchId }: { matchId: string }) {
  const [analysis, setAnalysis] = useState<MatchAnalysis | null>(null);
  const [probing, setProbing] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Read-only cache probe on open — never triggers a model call.
  useEffect(() => {
    setProbing(true);
    setAnalysis(null);
    setError(null);
    getMatchAnalysis(matchId)
      .then(setAnalysis)
      .catch((e) => {
        // 404 = no saved analysis for the current attempt — show the Analyze button.
        if (!(e instanceof ApiError && e.status === 404)) {
          setError(e instanceof Error ? e.message : String(e));
        }
      })
      .finally(() => setProbing(false));
  }, [matchId]);

  async function analyze(force: boolean) {
    setGenerating(true);
    setError(null);
    try {
      setAnalysis(await postAdminAnalyze(matchId, force));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setGenerating(false);
    }
  }

  function discuss() {
    if (!analysis) return;
    navigate(`/analyst?matchId=${encodeURIComponent(matchId)}`, {
      state: { report: analysis.analysisMd },
    });
  }

  if (probing) {
    return (
      <div className="text-gray-500 animate-pulse text-sm">Checking for saved analysis…</div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
          {error}
        </div>
      )}

      {!analysis ? (
        <div className="card space-y-3">
          <h3 className="font-semibold text-gray-700">AI Analysis</h3>
          {generating ? (
            <div className="text-gray-500 animate-pulse text-sm">
              Analyzing… this can take up to 60 s
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600">
                No saved analysis for this match's current attempt. Generate a full breakdown of
                the score arithmetic, layer discrepancies, and missing evidence.
              </p>
              <button type="button" onClick={() => analyze(false)} className="btn btn-primary">
                Analyze with AI
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="card">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <h3 className="font-semibold text-gray-700">AI Analysis</h3>
            <div className="flex items-center gap-2">
              <button type="button" onClick={discuss} className="btn btn-primary">
                Discuss
              </button>
              <button
                type="button"
                onClick={() => analyze(true)}
                disabled={generating}
                className="btn btn-secondary disabled:opacity-50"
              >
                Re-analyze
              </button>
            </div>
          </div>
          {generating && (
            <div className="text-gray-500 animate-pulse text-sm mt-3">
              Analyzing… this can take up to 60 s
            </div>
          )}
          <div className="mt-3">
            <MarkdownView>{analysis.analysisMd}</MarkdownView>
          </div>
          <div className="text-xs text-gray-400 border-t border-gray-100 pt-2 mt-3">
            {analysis.model} · prompt v{analysis.promptVersion} · attempt {analysis.attempt} ·{' '}
            {new Date(analysis.createdAt).toLocaleString()}
            {analysis.cached && ' · cached'}
          </div>
        </div>
      )}
    </div>
  );
}
